import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Printer, Loader2 } from "lucide-react";
import { toast } from "sonner";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { me as meApi, printCards as printCardsApi, plants as plantsApi, apiClient } from "@/lib/api";
import { exportPrintCardsPdf } from "@/lib/printCardsExport";

/**
 * Owner-only "Print info cards" panel for the Settings page.
 *
 * Renders nothing for non-owners (backend `GET /api/me/is-owner` returns
 * `is_owner: false`). Owners see one button that opens a dialog with:
 *   • All my plants
 *   • By tag → dropdown of the user's own tags
 * On confirm we mint public share tokens for the selected scans, then
 * generate a printable US-Letter PDF (two 5×7" landscape cards per page,
 * with binding zone + crop marks + QR codes) via `exportPrintCardsPdf`.
 */
export default function PrintCardsSection() {
  const { t } = useTranslation();
  const [isOwner, setIsOwner] = useState(false);
  const [open, setOpen] = useState(false);
  const [scope, setScope] = useState("all"); // "all" | "tag"
  const [selectedTag, setSelectedTag] = useState("");
  const [tags, setTags] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    meApi.isOwner()
      .then((r) => { if (!cancelled) setIsOwner(!!r?.data?.is_owner); })
      .catch(() => { /* silently non-owner */ });
    return () => { cancelled = true; };
  }, []);

  // Lazy-load tags only when the dialog opens — no need to fetch on every
  // Settings visit for non-power-users. The `/plants/tags` endpoint returns
  // `{ items: [{ tag: string, count: number }, ...] }` (matches the other
  // aggregation endpoints), NOT a flat string array. Coerce down to strings
  // here so downstream sort/render code never has to guard shape.
  useEffect(() => {
    if (!open) return;
    plantsApi.tags()
      .then((r) => {
        const raw = Array.isArray(r?.data?.items) ? r.data.items : [];
        const normalized = raw
          .map((entry) => (typeof entry === "string" ? entry : entry?.tag))
          .filter((s) => typeof s === "string" && s.trim().length > 0);
        setTags(normalized);
      })
      .catch(() => setTags([]));
  }, [open]);

  const tagOptions = useMemo(() => {
    // Surface the two obvious ones (front yard / back yard) first if the
    // user has them — matches Andrea's own tag conventions. Coerce to
    // strings again defensively so a stray non-string can't crash the memo.
    const priority = ["front yard", "back yard"];
    const asStrings = tags
      .map((tg) => (typeof tg === "string" ? tg : String(tg?.tag ?? "")))
      .filter(Boolean);
    const lower = asStrings.map((tg) => tg.toLowerCase());
    const priorityFirst = priority
      .filter((p) => lower.includes(p))
      .map((p) => asStrings.find((tg) => tg.toLowerCase() === p) || p);
    const rest = asStrings
      .filter((tg) => !priority.includes(tg.toLowerCase()))
      .sort((a, b) => a.localeCompare(b));
    return [...priorityFirst, ...rest];
  }, [tags]);

  if (!isOwner) return null;

  const handleGenerate = async () => {
    if (busy) return;
    setBusy(true);
    try {
      // 1. Get the list of scan_ids in scope. We hit the same /plants list
      //    endpoint the Gallery uses so filtering semantics are identical.
      const listResp = await apiClient.get("/plants");
      const items = Array.isArray(listResp?.data?.items) ? listResp.data.items : [];
      let filtered = items;
      if (scope === "tag") {
        const t2 = String(selectedTag || "").toLowerCase();
        if (!t2) {
          toast.error(t("printCards.pickTag") || "Pick a tag");
          setBusy(false);
          return;
        }
        filtered = items.filter((it) => Array.isArray(it.tags) && it.tags.map((x) => String(x).toLowerCase()).includes(t2));
      }
      if (filtered.length === 0) {
        toast.error(t("printCards.noMatches") || "No plants matched your selection");
        setBusy(false);
        return;
      }
      const scanIds = filtered.map((it) => it.scan_id);

      // 2. Mint share tokens for each scan.
      const tokensResp = await printCardsApi.createTokens(scanIds);
      const tokenMap = new Map(
        (tokensResp?.data?.cards || []).map((c) => [c.scan_id, c.share_token])
      );

      // 3. Fetch each scan's FULL payload so we have `image_base64` (not the
      //    ~200px thumbnail from the list endpoint). Thumbnails print blurry
      //    even at the modest 220pt card slot because printers scale to
      //    300+ DPI. We batch requests with a small concurrency limit so we
      //    don't overwhelm Cloudflare on a big print job, and update a
      //    single loading toast as we go.
      const CONCURRENCY = 4;
      const loadingToastId = toast.loading(
        t("printCards.loadingImages", { done: 0, total: scanIds.length })
        || `Loading images 0/${scanIds.length}…`
      );
      const fullById = new Map();
      let done = 0;
      const queue = [...scanIds];
      const worker = async () => {
        while (queue.length) {
          const sid = queue.shift();
          try {
            const r = await apiClient.get(`/plants/${sid}`);
            if (r?.data) fullById.set(sid, r.data);
          } catch {
            // Non-fatal: fall back to the thumbnail already on the plant obj.
          }
          done += 1;
          toast.loading(
            t("printCards.loadingImages", { done, total: scanIds.length })
            || `Loading images ${done}/${scanIds.length}…`,
            { id: loadingToastId }
          );
        }
      };
      await Promise.all(Array.from({ length: Math.min(CONCURRENCY, scanIds.length) }, worker));
      toast.dismiss(loadingToastId);

      const cards = filtered
        .map((plant) => {
          const token = tokenMap.get(plant.scan_id);
          if (!token) return null;
          // Prefer the full payload we just fetched; fall back to the list
          // item (thumb only) if the fetch failed.
          const full = fullById.get(plant.scan_id) || {};
          return {
            plant: { ...plant, ...full },
            shareUrl: `${window.location.origin}/share/${token}`,
          };
        })
        .filter(Boolean);

      if (cards.length === 0) {
        toast.error(t("printCards.tokenFail") || "Could not create share tokens");
        setBusy(false);
        return;
      }

      // 4. Generate + download PDF.
      const filename = scope === "tag"
        ? `prairie_info_cards_${selectedTag.replace(/\s+/g, "_").toLowerCase()}.pdf`
        : "prairie_info_cards_all.pdf";
      await exportPrintCardsPdf(cards, {
        language: (typeof window !== "undefined" && window.localStorage?.getItem("i18nextLng")) || "en",
        t,
        filename,
      });
      toast.success(t("printCards.done", { count: cards.length }) || `${cards.length} info card(s) generated`);
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("printCards.error") || "Could not generate info cards");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="print-cards-open"
        className="w-full flex items-center gap-3 px-5 py-4 hover:bg-parchment transition text-leaf-ink text-left"
      >
        <span className="text-leaf">
          <Printer className="w-4 h-4" strokeWidth={1.5} />
        </span>
        <span className="flex-1 flex flex-col">
          <span>{t("printCards.entryLabel") || "Print info cards (owner)"}</span>
          <span className="text-[11px] text-leaf-mist mt-0.5">
            {t("printCards.entryHint") || "5×7 landscape · two per US Letter · QR to public share"}
          </span>
        </span>
        <span className="text-leaf-mist">›</span>
      </button>

      <Dialog open={open} onOpenChange={(v) => (!busy && setOpen(v))}>
        <DialogContent data-testid="print-cards-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t("printCards.dialogTitle") || "Print info cards"}</DialogTitle>
            <DialogDescription>
              {t("printCards.dialogDesc") || "Generates a US-Letter PDF with two 5×7 in. landscape cards per page, room for a ringed binding at the top, and cut marks so a print shop can trim them cleanly."}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <label className="text-xs uppercase tracking-[0.14em] text-leaf-mist">
                {t("printCards.scope") || "Which plants?"}
              </label>
              <div className="flex flex-col gap-2">
                <label className="inline-flex items-center gap-2 text-sm text-leaf-ink">
                  <input
                    type="radio"
                    name="print-cards-scope"
                    value="all"
                    checked={scope === "all"}
                    onChange={() => setScope("all")}
                    data-testid="print-cards-scope-all"
                  />
                  {t("printCards.scopeAll") || "All my plants"}
                </label>
                <label className="inline-flex items-center gap-2 text-sm text-leaf-ink">
                  <input
                    type="radio"
                    name="print-cards-scope"
                    value="tag"
                    checked={scope === "tag"}
                    onChange={() => setScope("tag")}
                    data-testid="print-cards-scope-tag"
                  />
                  {t("printCards.scopeByTag") || "By tag"}
                </label>
              </div>
            </div>

            {scope === "tag" && (
              <div className="space-y-1.5">
                <label className="text-xs uppercase tracking-[0.14em] text-leaf-mist">
                  {t("printCards.tagLabel") || "Tag"}
                </label>
                <Select value={selectedTag} onValueChange={setSelectedTag}>
                  <SelectTrigger data-testid="print-cards-tag-select" className="w-full">
                    <SelectValue placeholder={t("printCards.tagPlaceholder") || "Pick a tag…"} />
                  </SelectTrigger>
                  <SelectContent>
                    {tagOptions.length === 0 ? (
                      <div className="px-3 py-2 text-xs text-leaf-mist">{t("printCards.noTags") || "No tags yet"}</div>
                    ) : (
                      tagOptions.map((tg) => (
                        <SelectItem key={tg} value={tg} data-testid={`print-cards-tag-option-${tg}`}>
                          {tg}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          <DialogFooter>
            <button
              type="button"
              onClick={() => setOpen(false)}
              disabled={busy}
              data-testid="print-cards-cancel"
              className="rounded-full bg-white border border-[#E8E5DF] text-leaf-ink px-4 py-2 text-sm hover:bg-parchment disabled:opacity-60 transition"
            >
              {t("common.cancel") || "Cancel"}
            </button>
            <button
              type="button"
              onClick={handleGenerate}
              disabled={busy || (scope === "tag" && !selectedTag)}
              data-testid="print-cards-generate"
              className="rounded-full bg-leaf text-white px-4 py-2 text-sm hover:bg-leaf-deep disabled:opacity-60 transition inline-flex items-center gap-1.5"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} /> : <Printer className="w-4 h-4" strokeWidth={1.5} />}
              {busy ? (t("printCards.generating") || "Generating…") : (t("printCards.generate") || "Generate PDF")}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
